import { useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { ListSubheader, MenuItem, Select } from '@mui/material';
import { get } from 'lodash';

import { CheckmarkIcon } from '@/common/icons/CheckmarkIcon';
import { DDDownIcon } from '@/common/icons/DDDownIcon';
import { CommonProps } from '@mui/material/OverridableComponent';
import { BaseNote, CustomerNote, NotificationNote } from './Types';

const useStyles: any = makeStyles(() => ({
  sortFilterSelect: {
    color: '#055577',
    fontSize: 14,
    height: 16,
    lineHeight: '20px',
    fontWeight: 600,
    '& fieldset': {
      border: 'none'
    },
    '& .MuiSelect-icon': {
      lineHeight: '14px'
    }
  },
  '& .MuiMenu-list': {
    width: 200
  },
  menuHeading: {
    fontSize: 12,
    lineHeight: '24px',
    color: '#999'
  },
  sortMenuItem: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '16px',
    '& span': {
      position: 'absolute',
      right: 8
    },
    textTransform: 'capitalize'
  },
  filterMenuItem: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '16px',
    '& span': {
      position: 'absolute',
      right: 8
    },
    textTransform: 'capitalize'
  }
}));

export type ListSortOption = {
  value: number;
  display: string;
  sortFn: (
    a: CustomerNote | NotificationNote,
    b: CustomerNote | NotificationNote
  ) => number;
};

export type ListFilterOption = {
  value: number;
  display: string;
  filterFn: (s: BaseNote) => boolean;
};

export const getNoteFilterFn = (categoryId: number) => (n: BaseNote) => {
  return n.categoryId === categoryId;
};

export const getSortOnFieldFn =
  (fieldName: string, direction: -1 | 1) =>
  (a: BaseNote | NotificationNote, b: BaseNote | NotificationNote) => {
    const aVal = String(get(a, fieldName))?.toLocaleLowerCase();
    const bVal = String(get(b, fieldName))?.toLocaleLowerCase();
    if (aVal > bVal) {
      return 1 * direction;
    } else if (aVal < bVal) {
      return -1 * direction;
    } else {
      return 0;
    }
  };

const filterSortSelectionDisplay = (
  sort: ListSortOption,
  filter: ListFilterOption
) => {
  const displayElements = [];
  if (sort) {
    displayElements.push(`Sort by: ${sort.display}`);
  }
  if (filter) {
    const filterNameDisplay =
      filter.display[0].toUpperCase() + filter.display.slice(1);
    displayElements.push(`Filter by: ${filterNameDisplay}`);
  }
  return displayElements.length
    ? displayElements.join(', ')
    : '(no filter selected)';
};

export interface ListSortFilterProps {
  setFilterSelection: (option: ListFilterOption) => void;
  setSortSelection: (option: ListSortOption) => void;
  filterSelections?: ListFilterOption[];
  sortSelections?: ListSortOption[];
}

/* When the time comes that we want another instance of this let's abstract it as a reusable/configurable component */
const ListSortFilter = (props: ListSortFilterProps) => {
  const {
    setFilterSelection,
    setSortSelection,
    filterSelections = [],
    sortSelections = []
  } = props;

  const classes = useStyles();

  const [filter, setFilter] = useState<ListFilterOption | null>(null);
  const [sort, setSort] = useState<ListSortOption>(sortSelections[0]);
  const [sortFilterOpen, setSortFilterOpen] = useState<boolean>(false);

  const onViewOptionChange = e => {
    setSortFilterOpen(false);
    const optionIndex = e.target.value[1];
    const item: ListSortOption | ListFilterOption = [
      ...filterSelections,
      ...sortSelections
    ].find(s => s.value === optionIndex);
    if ('filterFn' in item) {
      if (filter === item) {
        setFilter(null);
        setFilterSelection(null);
      } else {
        setFilter(item);
        setFilterSelection(item);
      }
    } else {
      setSort(item);
      setSortSelection(item);
    }
  };

  useEffect(() => {
    setSortSelection(sortSelections[0]);
  }, []);

  return (
    <Select
      className={classes.sortFilterSelect}
      multiple
      value={[sort]}
      onChange={onViewOptionChange}
      open={sortFilterOpen}
      onOpen={() => setSortFilterOpen(true)}
      onClose={() => setSortFilterOpen(false)}
      renderValue={() => filterSortSelectionDisplay(sort, filter)}
      IconComponent={(props: CommonProps) => {
        return <DDDownIcon {...props} />;
      }}
      MenuProps={{
        style: {
          zIndex: 10001
        },
        PaperProps: {
          sx: {
            borderRadius: '4px',
            minWidth: '300px !important',
            width: '300px !important',
            zIndex: 10001
          }
        }
      }}
    >
      {sortSelections.length > 0 && (
        <ListSubheader className={classes.menuHeading}>Sort by</ListSubheader>
      )}
      {sortSelections.map((s: ListSortOption, index: number) => (
        <MenuItem
          className={classes.sortMenuItem}
          key={index}
          value={s.value}
          data-my-value={s}
        >
          {s.display}
          {s === sort && <CheckmarkIcon />}
        </MenuItem>
      ))}
      {filterSelections.length > 0 && (
        <ListSubheader className={classes.menuHeading}>
          {'Filter by'}
        </ListSubheader>
      )}
      {filterSelections.map((f: ListFilterOption, index: number) => (
        <MenuItem
          className={classes.filterMenuItem}
          key={index}
          value={f.value}
          data-my-value={f}
        >
          {f.display}
          {f === filter && <CheckmarkIcon />}
        </MenuItem>
      ))}
    </Select>
  );
};

export default ListSortFilter;
